import { getRouteApi } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import CancelIcon from '@/assets/icons/cancel.svg?react'
import EditIcon from '@/assets/icons/edit.svg?react'
import HistoryIcon from '@/assets/icons/history.svg?react'
import DataBlock from '@/components/DataBlock/DataBlock'
import LinkText from '@/components/LinkText/LinkText'
import Table, { type Column } from '@/components/Table/Table'
import { TABLE_CELL_WIDTH } from '@/components/Table/table-cell-width'
import type { Action } from '@/components/common/BasicLayout/Actions'
import BasicLayout from '@/components/common/BasicLayout/BasicLayout'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import { toast } from '@/hooks/useToast'
import { joinNameArray } from '@/utils/join-array'

import styles from './CourseDetailsView.module.scss'
import ArchiveCourseModal from '../components/ArchiveCourseModal'
import RemoveCourseModal from '../components/RemoveCourseModal'
import { useCourse } from '../queries/useCourse'
import type { SimpleCourse } from '../types/simple-course'

const routeApi = getRouteApi('/_auth/courses-management/$courseId/')

const CourseDetailsView = () => {
  const { courseId } = routeApi.useParams()
  const navigate = routeApi.useNavigate()

  const { t } = useTranslation(['coursesManagement', 'common'])

  const [removeCourseModal, setRemoveCourseModal] =
    useState<SimpleCourse | null>(null)
  const [archiveCourseModal, setArchiveCourseModal] =
    useState<SimpleCourse | null>(null)

  const { data: course } = useCourse(courseId)

  const actions: Action[] = [
    {
      text: t('button.edit'),
      icon: <EditIcon />,
      asLink: true,
      to: '/courses-management/$courseId/edit',
      params: {
        courseId
      }
    },
    {
      text: t('button.archive-course'),
      icon: <HistoryIcon />,
      onClick: () => {
        if (!course) return

        setArchiveCourseModal(course)
      }
    },
    {
      text: t('button.remove'),
      icon: <CancelIcon />,
      variantAction: 'danger',
      onClick: () => {
        if (!course) return
        if (!course.canBeDeleted) {
          toast({
            variant: 'error',
            title: t('toast.course-can-not-be-removed.title', {
              COURSE: course.displayName
            }),
            description: t('toast.course-can-not-be-removed.description')
          })
          return
        }

        setRemoveCourseModal(course)
      }
    }
  ]

  const handleActionSuccess = () => {
    navigate({
      to: '/courses-management',
      search: {
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE
      }
    })
  }

  const columns: Column<{ id: string; name: string; semesters: number[] }>[] = [
    {
      key: 'name',
      label: t('header.name'),
      width: TABLE_CELL_WIDTH.LG,
      render: ({ id, name }) => (
        <LinkText to="/curricula/$curriculumId" params={{ curriculumId: id }}>
          {name}
        </LinkText>
      )
    },
    {
      key: 'semesters',
      label: t('header.semesters'),
      width: TABLE_CELL_WIDTH.LG,
      render: ({ semesters }) => semesters.join(', ')
    }
  ]

  return (
    <BasicLayout
      breadcrumbs={[
        {
          title: t('common:navigation.courses-management'),
          to: '/courses-management'
        },
        {
          title: t('header.course-details'),
          to: '/courses-management/$courseId',
          params: {
            courseId
          }
        }
      ]}
      subheader={t('header.course-details')}
      header={course?.displayName ?? ''}
      actions={actions}
      moduleName={t('common:header.administration')}
      actionsAsDropdown
    >
      <section className={styles.section}>
        <h2 className={styles.header}>{t('header.basic-information')}</h2>
        <div className={styles.informations}>
          <DataBlock
            infoArray={[
              {
                label: t('label.education-level'),
                value: course?.educationLevel.name
              },
              {
                label: t('label.domain'),
                value: course?.domain.name
              },
              {
                label: t('label.preferred-rooms'),
                value: joinNameArray(course?.preferredRooms)
              }
            ]}
          />
        </div>
      </section>
      <section className={styles.section}>
        <h2 className={styles.header}>{t('header.curricula')}</h2>
        <Table
          id="course-curriculums"
          data={course?.curriculums ?? []}
          columns={columns}
          showTopBar={false}
          caption={t('header.curricula')}
        />
      </section>
      {archiveCourseModal ? (
        <ArchiveCourseModal
          course={archiveCourseModal}
          onOpenChange={setArchiveCourseModal}
          onArchiveSuccess={handleActionSuccess}
        />
      ) : null}
      {removeCourseModal ? (
        <RemoveCourseModal
          course={removeCourseModal}
          onOpenChange={setRemoveCourseModal}
          onRemoveSuccess={handleActionSuccess}
        />
      ) : null}
    </BasicLayout>
  )
}

export default CourseDetailsView
